import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useOrderPanelStore } from '../../stores/order-panel-store'

/**
 * Gegevens-stap voor nieuwe klanten in de telefoonflow — verschijnt pas ná de
 * afhandelingskeuze en schrijft live naar de order-panel-store (updateCustomer)
 * zodat submitOrder altijd de actuele klant meestuurt.
 *
 * - variant 'vol' (Direct inboeken): naam, e-mail/telefoon, huisnummer →
 *   PDOK-prefill op de al-bekende postcode, straat, plaats.
 * - variant 'licht' (Offerte / Afgezien): alleen naam, e-mail, telefoon.
 *   Bij offerte is e-mail verplicht (de offerte gaat per mail); adres volgt
 *   later via het accepteer-pad.
 * - addressOptional (walk-in): variant 'vol' zonder adres-eis. De postcode is
 *   dan zelf invulbaar (stap 1 leverde er geen) en puur administratief — de
 *   prijs staat vast op het wasserij-gebied, want er rijdt niets.
 */

const inputClass =
  'w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession()
  const h: Record<string, string> = {}
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`
  return h
}

export function KlantGegevensForm({
  variant,
  requireEmail = false,
  addressOptional = false,
}: {
  variant: 'vol' | 'licht'
  /** Offerte-pad: e-mail verplicht (de offerte wordt per mail verstuurd). */
  requireEmail?: boolean
  /** Walk-in: adres mag, maar hoeft niet — en de postcode is zelf invulbaar. */
  addressOptional?: boolean
}) {
  const customer = useOrderPanelStore((s) => s.customer)
  const updateCustomer = useOrderPanelStore((s) => s.updateCustomer)

  // Huisnummer/straat lokaal — de store kent alleen het samengestelde street-veld.
  const [houseNumber, setHouseNumber] = useState('')
  const [houseNumberSuffix, setHouseNumberSuffix] = useState('')
  const [street, setStreet] = useState('')
  const [city, setCity] = useState(customer?.city ?? '')
  const [pdokStatus, setPdokStatus] = useState<'idle' | 'loading' | 'found' | 'not_found'>('idle')

  const postcode = customer?.postcode ?? ''
  // Buitenland: PDOK kent alleen NL-adressen — sla de lookup over en laat
  // kantoor straat/plaats handmatig invullen (de not_found-fallback bestaat al).
  const isBuitenland = !!customer?.country && customer.country !== 'Nederland'

  // PDOK-prefill zodra huisnummer is ingevuld (postcode is al bekend uit stap 1);
  // alles blijft overschrijfbaar (PDOK kan ontbreken voor nieuwbouw).
  useEffect(() => {
    if (variant !== 'vol' || !postcode || !houseNumber.trim() || isBuitenland) {
      setPdokStatus(isBuitenland && houseNumber.trim() ? 'not_found' : 'idle')
      return
    }
    let cancelled = false
    setPdokStatus('loading')
    const timer = setTimeout(async () => {
      try {
        const headers = await authHeaders()
        const response = await fetch(
          `/api/lookup?pc=${encodeURIComponent(postcode)}&hn=${encodeURIComponent(houseNumber.trim())}`,
          { headers }
        )
        if (cancelled) return
        if (!response.ok) {
          setPdokStatus('not_found')
          return
        }
        const data = await response.json()
        if (cancelled) return
        if (data?.ok && data.street && data.city) {
          setStreet(data.street as string)
          setCity(data.city as string)
          setPdokStatus('found')
        } else {
          setPdokStatus('not_found')
        }
      } catch {
        if (!cancelled) setPdokStatus('not_found')
      }
    }, 400)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [variant, postcode, houseNumber, isBuitenland])

  // Samengesteld adres → store, zodra de delen wijzigen.
  useEffect(() => {
    if (variant !== 'vol') return
    const fullStreet = street.trim() && houseNumber.trim()
      ? `${street.trim()} ${houseNumber.trim()}${houseNumberSuffix.trim()}`
      : ''
    updateCustomer({ street: fullStreet, city: city.trim() })
    // updateCustomer is stabiel (zustand); alleen op veld-wijzigingen reageren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, street, houseNumber, houseNumberSuffix, city])

  if (!customer) return null

  return (
    <div className="space-y-3">
      <label className="text-xs font-medium text-slate-600 block">
        {variant === 'vol' ? 'Klantgegevens' : 'Contactgegevens'}
      </label>
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1.5 block">
          Bedrijfsnaam <span className="font-normal text-slate-400">(optioneel — alleen bij zakelijke klant)</span>
        </label>
        <input
          type="text"
          value={customer.companyName ?? ''}
          onChange={(e) => updateCustomer({ companyName: e.target.value })}
          className={inputClass}
          placeholder="Laat leeg voor een particulier"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Voornaam</label>
          <input
            type="text"
            value={customer.firstName}
            onChange={(e) => updateCustomer({ firstName: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Achternaam *</label>
          <input
            type="text"
            value={customer.lastName}
            onChange={(e) => updateCustomer({ lastName: e.target.value })}
            className={inputClass}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">
            E-mail{requireEmail ? ' *' : ''}
          </label>
          <input
            type="email"
            value={customer.email}
            onChange={(e) => updateCustomer({ email: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">
            Telefoon{variant === 'vol' ? ' *' : ''}
          </label>
          <input
            type="tel"
            value={customer.phone}
            onChange={(e) => updateCustomer({ phone: e.target.value })}
            className={inputClass}
          />
        </div>
      </div>
      <p className="text-xs text-slate-400 -mt-1">
        {variant === 'vol'
          ? '* Telefoonnummer is verplicht — een opdracht vereist een bereikbaar nummer.'
          : requireEmail
            ? 'E-mail is verplicht — de offerte wordt per e-mail verstuurd.'
            : '* e-mail óf telefoon is verplicht'}
      </p>

      {variant === 'vol' && (
        <>
          {addressOptional && (
            <p className="text-xs text-slate-500 pt-1">
              Adres is optioneel — de klant brengt en haalt zelf. Vul het alleen in
              als het op de factuur of de klantkaart moet staan; het verandert de
              prijs niet.
            </p>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Postcode</label>
              <input
                type="text"
                value={postcode}
                disabled={!addressOptional}
                onChange={
                  addressOptional
                    ? (e) => updateCustomer({ postcode: e.target.value.replace(/\s+/g, '').toUpperCase() })
                    : undefined
                }
                className={addressOptional ? inputClass : `${inputClass} bg-slate-50 text-slate-500`}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                Huisnummer{addressOptional ? '' : ' *'}
              </label>
              <input
                type="text"
                value={houseNumber}
                onChange={(e) => setHouseNumber(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Toevoeging</label>
              <input
                type="text"
                value={houseNumberSuffix}
                onChange={(e) => setHouseNumberSuffix(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                Straat{addressOptional ? '' : ' *'}
                {pdokStatus === 'loading' && <span className="ml-2 text-slate-400 font-normal">zoeken...</span>}
                {pdokStatus === 'found' && <span className="ml-2 text-emerald-600 font-normal">✓ gevonden</span>}
              </label>
              <input type="text" value={street} onChange={(e) => setStreet(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                Plaats{addressOptional ? '' : ' *'}
              </label>
              <input type="text" value={city} onChange={(e) => setCity(e.target.value)} className={inputClass} />
            </div>
          </div>
          {pdokStatus === 'not_found' && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              Adres niet gevonden via postcode-lookup — vul straat en plaats handmatig in.
            </p>
          )}
        </>
      )}
    </div>
  )
}
